import React, { useEffect, useRef, useState } from "react";
import "./MediaDetailView.scss";
import { ImageType } from "../../../types/app/mediaTypes";
import { FaArrowLeft } from "react-icons/fa";
import * as Yup from "yup";
import { BiCopy } from "react-icons/bi";
import { Formik, Form, FieldArray } from "formik";
import { useImageDownloader } from "../../../shared/utils";



type Props = {
    show: boolean;
    imageData: ImageType | null;
    isPrev: boolean;
    isNext: boolean;
    onPrev: () => void;
    onNext: () => void;
    onClose: () => void;
    handleDeleteImages: (imageFilenames: string[]) => Promise<{
        isSuccess: boolean;
        isError: boolean;
    }>
    handleUpdateImage: ({ updatedImageData }: {
        updatedImageData: {
            id: string;
            url: string;
            names: string[];
            name: string | null;
            imageStatus: number;
        };
    }) => Promise<{
        isSuccess: boolean;
        isError: boolean;
    }>
};

const formatSize = (size?: number) => {
    if (!size || isNaN(size)) return "Unknown size";

    const units = ["bytes", "KB", "MB", "GB", "TB"];
    let unitIndex = 0;
    let displaySize = size;

    while (displaySize >= 1024 && unitIndex < units.length - 1) {
        displaySize /= 1024;
        unitIndex++;
    }

    const roundedSize = unitIndex === 0 ? displaySize : displaySize.toFixed(1);

    return `${roundedSize} ${units[unitIndex]}`;
};

const formatDate = (dateStr?: string) => {
    if (!dateStr) return "Unknown date";
    const date = new Date(dateStr);

    const month = date.getMonth() + 1;
    const day = date.getDate();
    const year = date.getFullYear();

    let hours = date.getHours();
    const minutes = date.getMinutes().toString().padStart(2, "0");
    const ampm = hours >= 12 ? "PM" : "AM";
    hours = hours % 12 || 12; // convert to 12-hour format

    return `${month}/${day}/${year} ${hours}:${minutes} ${ampm}`;
};


export const MediaDetailView: React.FC<Props> = ({
    show,
    imageData,
    isPrev,
    isNext,
    onPrev,
    onNext,
    onClose,
    handleDeleteImages,
    handleUpdateImage
}) => {
    const [editMode, setEditMode] = useState(false);
    const [loaded, setLoaded] = useState(false);
    const [hasError, setHasError] = useState(false);
    const imgRef = useRef<HTMLImageElement>(null);
    const placeholderRef = useRef<HTMLDivElement>(null);
    const [info, setInfo] = useState("");
    const [reloadKey, setReloadKey] = useState(0);
    const [copied, setCopied] = useState(false);
    const { loading, downloadImages, error } = useImageDownloader();

    const handleCopy = async () => {
        if (imageData?.id) {
            try {
                await navigator.clipboard.writeText(imageData.id);
                setCopied(true);
                setTimeout(() => setCopied(false), 5000);
            } catch (err) {
                console.error("Failed to copy: ", err);
            }
        }
    };

    const otherInfoSchema = Yup.object().shape({
        categories: Yup.array()
            .of(Yup.string().trim().required("Category cannot be empty"))
            .min(1, "Select at least one category"),
        otherNames: Yup.array()
            .of(Yup.string().trim().required("Other name cannot be empty"))
            .min(1, "Add at least one other name"),
    });

    const reloadImage = () => {
        setReloadKey(prev => prev + 1);
    };

    useEffect(() => {
        if (imgRef.current && imageData?.imageUrl) {
            imgRef.current.src = `${imageData.imageUrl}?t=${Date.now()}`;
            setLoaded(false);
            setHasError(false);
        }
    }, [reloadKey, imageData]);

    useEffect(() => {
        const targetRef = hasError
            ? placeholderRef.current
            : imgRef.current;
        if (!targetRef) return;

        const updateInfo = () => {
            let width: number;
            let height: number;

            if (targetRef instanceof HTMLImageElement) {
                width = targetRef.naturalWidth || targetRef.width;
                height = targetRef.naturalHeight || targetRef.height;
            } else {
                width = targetRef.offsetWidth;
                height = targetRef.offsetHeight;
            }
            setInfo(
                `${width} x ${height}px`
            );
        };
        // Initial run
        updateInfo();

        // Watch for size changes
        const observer = new ResizeObserver(updateInfo);
        observer.observe(targetRef);

        return () => observer.disconnect();
    }, [imageData, hasError]);

    const fileType = imageData?.imageUrl
        ? imageData?.imageUrl.split('.').pop()?.toLowerCase() || 'unknown'
        : 'unknown';

    if (!show || !imageData) return null;

    const handlePrevClick = () => {
        if (!isPrev) {
            onPrev();
            reloadImage()
            setEditMode(false)
        }
    };

    const handleNextClick = () => {
        if (!isNext) {
            onNext();
            reloadImage()
            setEditMode(false)
        }
    };




    return (
        <div className="image-viewer-overlay">
            {/* Prev Button */}
            <button
                className="nav-btn prev"
                onClick={handlePrevClick}
                disabled={isPrev}
            >
                <svg width="24" height="24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="15 18 9 12 15 6" />
                </svg>
            </button>

            {/* Next Button */}
            <button
                className="nav-btn next"
                onClick={handleNextClick}
                disabled={isNext}
            >
                <svg width="24" height="24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="9 18 15 12 9 6" />
                </svg>
            </button>

            <div className="image-viewer-container">
                {/* Back Button */}
                <button className="back-btn" onClick={onClose}>
                    <FaArrowLeft color="white" />
                </button>

                <div className="content">
                    {/* Image Column */}
                    <div className="image-column">
                        {/* Top right icon buttons */}
                        <div className="image-icons">
                            <button className="bulk-icon-btn" onClick={() => downloadImages(imageData.imageUrl)} disabled={loading}>
                                <svg width="30" height="30" fill="none" stroke={`${error ? 'red' : 'white'}`} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                                    <polyline points="7 10 12 15 17 10" />
                                    <line x1="12" y1="15" x2="12" y2="3" />
                                </svg>
                            </button>
                            <button className="bulk-icon-btn" onClick={() => setEditMode(true)}>
                                <svg width="30" height="30" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <path d="M12 20h9" />
                                    <path d="M16.5 3.5a2.121 2.121 0 1 1 3 3L7 19l-4 1 1-4 12.5-12.5z" />
                                </svg>
                            </button>
                            <button className="bulk-icon-btn" onClick={async () => {
                                const result = await handleDeleteImages([imageData?.name as string])

                                if (result.isSuccess) {
                                    onClose();
                                }

                            }} >
                                <svg width="30" height="30" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <polyline points="3 6 5 6 21 6" />
                                    <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6m5 0V4a1 1 0 0 1 1-1h2a1 1 0 0 1 1 1v2" />
                                </svg>
                            </button>
                        </div>
                        {/* Prev Button inside image column */}
                        <button className="nav-btn prev" onClick={handlePrevClick} disabled={isPrev}>
                            <svg width="24" height="24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <polyline points="15 18 9 12 15 6" />
                            </svg>
                        </button>

                        {/* Next Button inside image column */}
                        <button className="nav-btn next" onClick={handleNextClick} disabled={isNext}>
                            <svg width="24" height="24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <polyline points="9 18 15 12 9 6" />
                            </svg>
                        </button>
                        {!loaded && !hasError && <div className="shimmer" />}
                        <img
                            ref={imgRef}
                            src={`${imageData.imageUrl}?v=${reloadKey}`}
                            alt={imageData.name || "Image"}
                            width={600}
                            height={600}
                            onLoad={() => setLoaded(true)}
                            onError={() => setHasError(true)}
                            style={{
                                display: hasError || !loaded ? "none" : "block"
                            }}
                        />
                        {hasError && (
                            <div ref={placeholderRef} className="error-placeholder">
                                Image not available
                            </div>
                        )}
                    </div>

                    {/* Info Column */}
                    <div className="info-column">
                        <div className="inner-content">
                            <div className="name-section">
                                <div className="name-inner-section">
                                    <div className="image-name">
                                        {imageData.name || "No name"}
                                    </div>
                                    <div className="image-id">
                                        <div>
                                            {imageData?.id || "No ID"}
                                        </div>
                                        <button className="copy-icon" onClick={handleCopy}>
                                            <BiCopy color={copied ? "green" : "black"} strokeWidth={copied ? 2 : 1} />
                                        </button>
                                    </div>

                                </div>
                            </div>
                            <div className="stats-section">
                                <div className="size-type-section">
                                    {fileType.toUpperCase()} <div className="dot" /> {formatSize(imageData?.size)} <div className="dot" /> {info}
                                </div>
                                <div className="created-at-section">
                                    <div>Added</div>
                                    <div className="created-at-label">
                                        By {imageData.createdBy || 'Unknown'} on {formatDate(imageData.createdDate)}
                                    </div>

                                </div>
                            </div>
                            <div className="other-info-section">
                                <Formik
                                    enableReinitialize
                                    initialValues={{
                                        // categoryName: imageData?.categoryName || '',
                                        otherNames: imageData?.otherNames || [],
                                    }}
                                    validationSchema={otherInfoSchema}
                                    onSubmit={(values) => {
                                        console.log("Form Data", values);
                                        const payload = {
                                            id: imageData?.id,
                                            url: imageData?.imageUrl,
                                            names: values?.otherNames,
                                            name: imageData?.name,
                                            imageStatus: imageData?.imageType
                                        }
                                        handleUpdateImage({ updatedImageData: payload })
                                        setEditMode(false);
                                    }}
                                >
                                    {({ values, errors, touched, setFieldValue }) => (
                                        //@ts-ignore
                                        <Form as="div">
                                            {/* CATEGORY */}
                                            <div className="section">
                                                <div>Category</div>
                                                <div className="list">
                                                    <span>{imageData.categoryName}</span>
                                                </div>
                                            </div>
                                            <div className="section">
                                                <div>Other Names</div>
                                                {editMode ? (
                                                    <FieldArray name="otherNames">
                                                        {({ push, remove }) => (
                                                            <>
                                                                <div className="list">
                                                                    {values.otherNames.map((name, index) => (
                                                                        <span key={index}>
                                                                            {name}
                                                                            <button type="button" onClick={() => remove(index)}>
                                                                                X
                                                                            </button>
                                                                        </span>
                                                                    ))}
                                                                    <input
                                                                        type="text"
                                                                        placeholder="Add other names"
                                                                        onKeyDown={(e) => {
                                                                            //@ts-ignore
                                                                            if (e.key === "Enter" && e.target.value.trim()) {
                                                                                e.preventDefault();
                                                                                //@ts-ignore
                                                                                push(e.target.value.trim());
                                                                                //@ts-ignore
                                                                                e.target.value = "";
                                                                            }
                                                                        }}
                                                                    />
                                                                </div>
                                                                {touched.otherNames && errors.otherNames && (
                                                                    <div className="error-text">{errors.otherNames}</div>
                                                                )}
                                                            </>
                                                        )}
                                                    </FieldArray>
                                                ) : (
                                                    <div className="list">
                                                        {values.otherNames.map((name, index) => (
                                                            <span key={index}>{name}</span>
                                                        ))}
                                                    </div>
                                                )}
                                            </div>

                                            {editMode && (
                                                <div className="edit-actions">
                                                    <button
                                                        type="button"
                                                        className="discard-btn"
                                                        onClick={() => setEditMode(false)}
                                                    >
                                                        Discard All
                                                    </button>
                                                    <button type="submit" className="done-btn">
                                                        Done
                                                    </button>
                                                </div>
                                            )}
                                        </Form>
                                    )}
                                </Formik>

                            </div>
                            {/* {!editMode ? (
                                <>
                                    <h2>{imageData.name || "No name"}</h2>
                                    <div className="tags">
                                        <span className="tag">Vegetables</span>
                                        <span className="tag">Dairy</span>
                                    </div>
                                    <h4>Other names</h4>
                                    <div className="tags">
                                        {imageData.otherNames?.map((n, i) => (
                                            <span key={i} className="tag">{n}</span>
                                        ))}
                                    </div>
                                    <p>Size: {formatSize(imageData.size)}</p>
                                    <button className="edit-toggle-btn" onClick={() => setEditMode(true)}>Edit</button>
                                </>
                            ) : (
                                <>
                                    <input type="text" defaultValue={imageData.name || ""} placeholder="Name" className="text-input" />
                                    <label>Category</label>
                                    <div className="tags editable">
                                        <span className="tag">Vegetables</span>
                                        <span className="tag">Dairy</span>
                                    </div>
                                    <label>Other names</label>
                                    <input type="text" defaultValue={imageData.otherNames?.join(", ") || ""} placeholder="Other names" className="text-input" />
                                    <div className="edit-actions">
                                        <button className="discard-btn" onClick={() => setEditMode(false)}>Discard all</button>
                                        <button className="done-btn" onClick={() => setEditMode(false)}>Done</button>
                                    </div>
                                </>
                            )} */}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};